import { ChevronRight, MapPin } from 'lucide-react';
import {
  getServiceCallListCustomerName,
  getServiceCallListCityState,
  getStatusColor,
  getStatusText
} from '@shared/utils';

// Shared list primitives for the Today and Calendar agenda views so both
// pages render assignment cards identically.

export function Section({ title, icon: Icon, count, tone, emptyText, children }) {
  const hasItems = Array.isArray(children) ? children.length > 0 : !!children;
  const headerColor = tone === 'danger' ? 'text-red-600' : 'text-slate-700';

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <Icon className={`h-5 w-5 ${headerColor}`} />
        <h2 className={`text-sm font-semibold uppercase tracking-wide ${headerColor}`}>
          {title}
        </h2>
        {count != null && (
          <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
            {count}
          </span>
        )}
      </div>
      {hasItems ? (
        <div className="space-y-2">{children}</div>
      ) : (
        emptyText && <p className="text-sm text-slate-400">{emptyText}</p>
      )}
    </section>
  );
}

export function CallCard({ call, onClick, tone, subtitle, muted }) {
  const cityState = getServiceCallListCityState(call);
  const borderClass = tone === 'danger'
    ? 'border-red-200 bg-red-50'
    : muted
      ? 'border-slate-100 bg-slate-50'
      : 'border-slate-200 bg-white';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-2xl border ${borderClass} p-3 text-left shadow-sm transition-all hover:shadow-md active:scale-[0.99]`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={`truncate text-base font-semibold ${muted ? 'text-slate-500' : 'text-slate-900'}`}>
            {getServiceCallListCustomerName(call)}
          </p>
          {call.isPriority && (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
              Priority
            </span>
          )}
        </div>
        {cityState && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-sm text-slate-500">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            {cityState}
          </p>
        )}
        <div className="mt-1.5 flex items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(call.status)}`}>
            {getStatusText(call.status)}
          </span>
          {call.type?.name && (
            <span className="text-xs text-slate-400">{call.type.name}</span>
          )}
          {subtitle && <span className="text-xs font-medium text-red-600">{subtitle}</span>}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 flex-shrink-0 text-slate-300" />
    </button>
  );
}
